import { PaymentMethod } from '@wix/cashier-common/dist/src/enums/payments/PaymentMethod';
import { PaymentService } from '@wix/cashier-core/dist/src/services/Payment';
import { RedirectTarget } from '@wix/cashier-core/dist/src/services/Payment/types';
import { ICashierPublicCallbacks } from '../../cashier-sdk';
import { PaymentMethodsEvents, PaymentMethodsStatuses } from '../../types';
import { MFieldsNested } from '../../types/MField';
import { ModulePayOptions } from '../../types/ModulePayOptions';
import { buildPaymentDetails } from '../../utils/buildPaymentDetails';
import { paymentPopup } from '../../utils/PaymentPopup';
import {
  PMethodModule,
  PMethodModuleConfiguration,
} from '../cashier-sdk-modules';
import PaymentModule from './PaymentModule';

interface WalletsConfiguration
  extends ICashierPublicCallbacks,
    PMethodModuleConfiguration {
  orderId: string;
  host?: string;
  paymentModule?: PaymentModule;
  locale: string;
  visitorId: string;
}

class Wallets implements PMethodModule {
  private props: WalletsConfiguration;
  public name: string;

  constructor(config: WalletsConfiguration) {
    const {
      paymentModule,
      methodId,
      orderId,
      onMessage,
      host,
      locale,
      visitorId,
    } = config;

    this.props = {
      paymentModule,
      host,
      methodId,
      orderId,
      onMessage,
      locale,
      visitorId,
    };
  }

  public pay = async (
    mFieldsNested: MFieldsNested,
    options: ModulePayOptions
  ) => {
    const {
      frameable = false,
      responsive = false,
      redirectable = true,
      paymentMethod,
      revalidateAppInstance,
      orderId = this.props.orderId,
    } = options;
    const redirectTarget = this.props.paymentModule.getRedirectTarget({
      paymentMethod,
      frameable,
    });
    const inNewTab =
      redirectTarget === RedirectTarget.NewWindow && redirectable;

    if (inNewTab && !paymentPopup.isOpened()) {
      this.props.paymentModule.preOpenPaymentPopup(paymentMethod);
    }

    options.onBeforeCharge();

    const chargeResponse = await new PaymentService({
      baseURL: this.props.host,
      appInstance: options.instance,
      revalidateAppInstance,
    }).chargeOrder(
      orderId,
      {
        payment: {
          details: await buildPaymentDetails({
            mFieldsNested,
            ...(redirectable && { redirectTarget }),
            paymentMethod,
            locale: this.props.locale,
            visitorId: this.props.visitorId,
          }),
        },
      },
      { fullInfo: false }
    );

    return this.props.paymentModule.continuePayment(paymentMethod, {
      chargeResponse,
      inNewTab,
      frameable,
      responsive,
      instance: options.instance,
    });
  };

  public validate = async (walletId: PaymentMethod) => {
    this.props.onMessage?.(walletId, {
      type: PaymentMethodsEvents.VALIDATION,
      isValid: true,
    });

    return { isValid: true };
  };

  public collapse = async (walletId: PaymentMethod) => ({
    method: walletId,
    type: PaymentMethodsEvents.COLLAPSE,
    status: PaymentMethodsStatuses.COLLAPSED,
  });

  public expand = async (walletId: PaymentMethod) => ({
    method: walletId,
    type: PaymentMethodsEvents.EXPAND,
    status: PaymentMethodsStatuses.EXPANDED,
  });
}

export default Wallets;
